import gsap from "gsap";
import { generateUID } from "verdi-common/utils/HelperFunctions";
import { INFO_CARD_TYPE } from "@/features/cards/shared/card-routing/card-types";
import { shouldBlockMapEntityCardOpen } from "@/features/add-device/should-block-map-entity-card-open";
import { openZustandCard } from "@/features/cards/shared/legacy/open-zustand-card";
import { useStore } from "@/store/store";
import { AreaOfInterestInfoCard } from "../../../features/cards/deprecated/info-cards/area-of-interest-info-card";
import ZoneIcon from "../../../icons/inline-react-svgs/zone-icon";
import FocusContext from "../focus-context";
import { addMapEntityPolygonToMap } from "../map-helper";
import MapEntityBase from "./map-entity-base";

const BASE_LINE_WEIGHT = 2.5;
const BASE_LINE_COLOR = "#0083bd";
const HIGHLIGHTED_LINE_COLOR = "#009de0";

export default class AreaofInterestMapEntity extends MapEntityBase {
    static infoCardClass = AreaOfInterestInfoCard;

    infoCardClass = AreaOfInterestInfoCard;

    /**
     *
     * @type {AreaOfInterest}
     */
    model = undefined;

    icon = ZoneIcon;

    /**
     * @param {Zone} aoi
     * @param {boolean} renderImmediately
     */
    constructor(aoi, renderImmediately = true) {
        super(aoi, generateUID("AOI"), false);
        this.model = aoi;
        const updateLatLongFromPolygon = () => {
            if (this.model.polygon && this.model.polygon.length > 0) {
                const result = getLatLongOfPolygon(this.model.polygon);
                this.lat = result.lat;
                this.long = result.long;
            }
        };
        try {
            if (this.model.polygon && this.model.polygon.length > 0) {
                updateLatLongFromPolygon();
                this.model.onChange.addListener(() => {
                    updateLatLongFromPolygon();
                    if (this.leafletElement) {
                        this.leafletElement.setLatLngs(this.model.polygon);
                    }
                });
            }
        } catch (e) {
            console.warn("Error setting center position of AOI:", e);
        }

        if (renderImmediately) {
            this.renderOnMap();
        }
    }

    renderOnMap() {
        if (!this.renderState.onMap && !this.renderState.beingRendered) {
            this.renderState.beingRendered = true;
            addMapEntityPolygonToMap(this, this.model.polygon)
                .then((r) => {
                    this.leafletElement = r.leafletPolygon;
                    this.leafletElement.on("click", (e) => {
                        // prevent FocusContext click intercept when repositioning a device
                        if (!useStore.getState().isRepositioningDevice) {
                            /**
                             * With some persistent cards (such as scheduler), we don't want a click on an AOI to open a new card
                             * and dismiss the existing card.
                             */
                            if (!shouldBlockMapEntityCardOpen(FocusContext.currentInfoCard?.currentInfoCardType)) {
                                openZustandCard({ entity: this, cardType: INFO_CARD_TYPE.AOI });
                            } else {
                                FocusContext.onInteraction(e, this);
                            }
                        }
                        this.leafletElement.bringToBack();
                    });
                    this.renderState.onMap = true;
                    this.renderState.beingRendered = false;
                    this.leafletElement.bringToBack();
                    this.updateFocusState();
                })
                .catch((e) => {
                    console.warn("Failed to render area of interest polygon", e);
                    this.renderState.beingRendered = false;
                    this.renderState.onMap = false;
                });
        }
    }

    updateFocusState() {
        super.updateFocusState();
        this.updateTweener();
    }

    propsOfLeaflet = {
        color: BASE_LINE_COLOR,
        weight: BASE_LINE_WEIGHT,
        fillOpacity: 0,
    };

    paintTweenUpdate() {
        if (this.leafletElement) {
            this.leafletElement.setStyle(this.propsOfLeaflet);
        }
    }

    updateTweener() {
        gsap.to(this.propsOfLeaflet, {
            overwrite: "auto",
            duration: 0.4,
            onUpdate: () => {
                this.paintTweenUpdate();
            },
            color: () =>
                ({
                    focused: HIGHLIGHTED_LINE_COLOR,
                    selected: HIGHLIGHTED_LINE_COLOR,
                    inactive: BASE_LINE_COLOR,
                })[this.focusState] || BASE_LINE_COLOR,
            weight: () =>
                ({
                    focused: BASE_LINE_WEIGHT + 2,
                    selected: BASE_LINE_WEIGHT + 0.5,
                    inactive: BASE_LINE_WEIGHT - 0.5,
                })[this.focusState] || BASE_LINE_WEIGHT,
            ease: "power1.inOut",
        });
    }
}

export function getLatLongOfPolygon(polygon) {
    const maxPoint = [-100000, -100000];
    const minPoint = [100000, 100000];
    polygon.forEach((vertice) => {
        maxPoint[0] = Math.max(maxPoint[0], vertice.lat);
        maxPoint[1] = Math.max(maxPoint[1], vertice.lng);
        minPoint[0] = Math.min(minPoint[0], vertice.lat);
        minPoint[1] = Math.min(minPoint[1], vertice.lng);
    });
    return {
        lat: (maxPoint[0] + minPoint[0]) / 2,
        long: (maxPoint[1] + minPoint[1]) / 2,
    };
}
